import { useEffect, useState } from 'react';
import { Button } from './Button';
import useI18n from '../../hooks/useI18n';

interface ServerUnreachableProps {
  /** Triggers one fresh load attempt. No auto-retry — one request per click. */
  onRetry: () => void;
}

/** Cooldown between retries so a user cannot hammer a struggling backend. */
const RETRY_COOLDOWN_SECONDS = 30;

// The cooldown is anchored to a timestamp in sessionStorage, NOT component
// state: clicking retry flips loading=true and ProtectedRoutes unmounts this
// page for <Loading/>; on failure a fresh mount appears. Local state would
// reset to 0 across that swap and the timer would never be visible.
const COOLDOWN_ENDS_AT_KEY = 'serverUnreachableCooldownEndsAt';

const getRemainingSeconds = () => {
  const endsAt = Number(sessionStorage.getItem(COOLDOWN_ENDS_AT_KEY) || 0);
  return Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
};

/**
 * Full-screen connection-error page, shown when the app cannot reach the
 * API but the session is intact (transient failure — see ProtectedRoutes'
 * loadError gate). Branded to the dashboard theme: Manrope/Inter, deep
 * primary #113E53 with the signature #59FF93 accent as a live radar pulse.
 */
const ServerUnreachable = ({ onRetry }: ServerUnreachableProps) => {
  const { t } = useI18n();
  const [cooldown, setCooldown] = useState(getRemainingSeconds);

  useEffect(() => {
    if (cooldown === 0) return undefined;
    // Recompute from the timestamp each tick — drift-proof and remount-proof.
    const tick = setTimeout(() => setCooldown(getRemainingSeconds()), 1000);
    return () => clearTimeout(tick);
  }, [cooldown]);

  const handleRetry = () => {
    if (getRemainingSeconds() > 0) return;
    sessionStorage.setItem(
      COOLDOWN_ENDS_AT_KEY,
      String(Date.now() + RETRY_COOLDOWN_SECONDS * 1000),
    );
    setCooldown(RETRY_COOLDOWN_SECONDS);
    onRetry();
  };

  return (
    <div className="relative flex h-screen flex-col items-center justify-center overflow-hidden bg-white px-6">
      {/* ── Atmosphere: dot grid + soft brand orbs ─────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(#113E53_0.5px,transparent_0.5px)] [background-size:24px_24px] opacity-[0.04]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
      />

      {/* ── Content (staggered entrance) ───────────────────────────────── */}
      <div className="relative flex flex-col items-center">
        <img
          src="/assets/Logo.svg"
          alt="Linkfluencer"
          className="mb-12 h-8 w-auto animate-fade-up motion-reduce:animate-none"
        />

        {/* Icon medallion with radar pings — the page "trying to reach" */}
        <div className="relative mb-8 flex h-20 w-20 animate-fade-up items-center justify-center [animation-delay:150ms] motion-reduce:animate-none">
          <span
            aria-hidden="true"
            className="absolute inline-flex h-full w-full animate-[ping_2.4s_cubic-bezier(0,0,0.2,1)_infinite] rounded-full bg-secondary/40 motion-reduce:animate-none"
          />
          <span
            aria-hidden="true"
            className="absolute inline-flex h-full w-full animate-[ping_2.4s_cubic-bezier(0,0,0.2,1)_infinite] rounded-full bg-secondary/30 [animation-delay:1.2s] motion-reduce:animate-none"
          />
          <div className="relative flex h-20 w-20 items-center justify-center rounded-full border border-primary/10 bg-white shadow-[0_8px_30px_rgba(17,62,83,0.12)]">
            {/* cloud-off icon in brand primary */}
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#113E53"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22.61 16.95A5 5 0 0 0 18 10h-1.26a8 8 0 0 0-7.05-6" />
              <path d="M5 5a8 8 0 0 0 4 15h9a5 5 0 0 0 1.7-.3" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </div>
        </div>

        <h1 className="animate-fade-up text-center font-header text-3xl font-extrabold tracking-tight text-primary [animation-delay:200ms] motion-reduce:animate-none">
          {t('errors.serverUnreachable.title')}
        </h1>

        <p className="mt-3 max-w-md animate-fade-up text-center font-content text-sm leading-6 text-gray-500 [animation-delay:300ms] motion-reduce:animate-none">
          {t('errors.serverUnreachable.description')}
        </p>

        <Button
          variant="contained"
          color="primary"
          disabled={cooldown > 0}
          className="mt-9 animate-fade-up px-10 py-2.5 font-content shadow-[0_4px_14px_rgba(17,62,83,0.25)] transition-transform hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50 [animation-delay:400ms] motion-reduce:animate-none"
          onClick={handleRetry}
        >
          {cooldown > 0
            ? t('errors.retryCountdown', { seconds: cooldown })
            : t('errors.retry')}
        </Button>
      </div>

      <span className="absolute bottom-8 font-content text-xs text-gray-400">
        {t('common.copyright')}
      </span>
    </div>
  );
};

export default ServerUnreachable;
